/**
 * Content Tier Context
 *
 * Provides tier-based content gating for DIY project pages.
 * Tiers: anon (not authenticated), free (authenticated), premium (paid subscription)
 *
 * Usage:
 * ```tsx
 * <ContentTierProvider>
 *   <ProjectGuidePage />
 * </ContentTierProvider>
 *
 * // In child components:
 * const { tier, canViewVideo, getVisibleTipCount } = useContentTier()
 * ```
 */

import {  createContext, useContext, useMemo } from 'react'
import { useAuth } from '@clerk/tanstack-react-start'
import type {ReactNode} from 'react';

export type ContentTier = 'anon' | 'free' | 'premium'

interface ContentTierContextValue {
  tier: ContentTier
  isLoading: boolean

  // Feature access helpers
  canViewVideo: boolean
  canViewAllTips: boolean
  canViewCommunityTips: boolean

  // Tip visibility calculation
  getVisibleTipCount: (totalTips: number) => number
}

const ContentTierContext = createContext<ContentTierContextValue | null>(null)

const FREE_TIP_COUNT = 2 // Tips visible to anon users

interface ContentTierProviderProps {
  children: ReactNode
  // Allow override for testing or specific contexts (e.g., preview mode)
  overrideTier?: ContentTier
  /** @internal test: `tier` still resolves, but `isLoading` is true (e.g. Clerk not loaded) */
  overrideLoading?: boolean
}

export function ContentTierProvider({
  children,
  overrideTier,
  overrideLoading,
}: ContentTierProviderProps) {
  const { isSignedIn, isLoaded } = useAuth()

  // TODO: Query user subscription status when premium tier is implemented
  // const subscription = useQuery(
  //   api.users.getSubscription,
  //   isSignedIn ? {} : 'skip'
  // )

  const value = useMemo<ContentTierContextValue>(() => {
    // Allow override for testing
    if (overrideTier) {
      const isPremium = overrideTier === 'premium'
      const isAuthed = overrideTier !== 'anon'
      return {
        tier: overrideTier,
        isLoading: overrideLoading === true,
        canViewVideo: isAuthed,
        canViewAllTips: isAuthed,
        canViewCommunityTips: isPremium,
        getVisibleTipCount: (total) => (isAuthed ? total : Math.min(total, FREE_TIP_COUNT)),
      }
    }

    // Loading state
    if (!isLoaded) {
      return {
        tier: 'anon',
        isLoading: true,
        canViewVideo: false,
        canViewAllTips: false,
        canViewCommunityTips: false,
        getVisibleTipCount: () => FREE_TIP_COUNT,
      }
    }

    // Anon user
    if (!isSignedIn) {
      return {
        tier: 'anon',
        isLoading: false,
        canViewVideo: false,
        canViewAllTips: false,
        canViewCommunityTips: false,
        getVisibleTipCount: (total) => Math.min(total, FREE_TIP_COUNT),
      }
    }

    // Authenticated user - check subscription
    // TODO: Replace with actual subscription check when premium tier is implemented
    // For now, all authenticated users are 'free' tier until premium is implemented

    return {
      tier: 'free' as ContentTier,
      isLoading: false,
      canViewVideo: true,
      canViewAllTips: true,
      canViewCommunityTips: false, // Premium feature - enable when subscription check is added
      getVisibleTipCount: (total) => total,
    }
  }, [isLoaded, isSignedIn, overrideLoading, overrideTier])

  return <ContentTierContext.Provider value={value}>{children}</ContentTierContext.Provider>
}

export function useContentTier(): ContentTierContextValue {
  const context = useContext(ContentTierContext)
  if (!context) {
    throw new Error('useContentTier must be used within ContentTierProvider')
  }
  return context
}

/**
 * Hook to check if content should be gated based on required tier
 */
export function useIsGated(requiredTier: ContentTier): boolean {
  const { tier } = useContentTier()

  const tierOrder: Record<ContentTier, number> = {
    anon: 0,
    free: 1,
    premium: 2,
  }

  return tierOrder[tier] < tierOrder[requiredTier]
}
